import { create } from "zustand";
import { localStorageStore } from "./localStorage.store";

export type AuthTokens = {
	accessToken: string;
	refreshToken: string;
	expiresAt: string;
};

export type Login = {
	isBrand: string;
	brandId: string;
	isSuperAdmin: string;
	brandName: string;
};

export interface AuthState {
	accessToken: string | null;
	refreshToken: string | null;
	expiresAt: string | null;
	userId: string | null;
	userName: string | null;
	brandDetails: string | null;
	scope: string | null;
	isForgotPasswordLoading: boolean;
	isLoggedIn: boolean;
	invitationToken: string | null;
	isBrand: string | null;
	brandId: string | null;
	isSuperAdmin: string | null;
	brandName: string | null;

	setAuth: (auth: AuthTokens) => void;
	setUser: (userId: string, userName: string) => void;
	setLogin: (login: Login) => void;
	setBrandDetails: (brandDetails: string) => void;
	setScope: (scope: string) => void;
	resetAuth: () => void;
	setIsLoggedIn: (isLoggedIn: boolean) => void;
	setIsForgotPasswordLoading: (isForgotPasswordLoading: boolean) => void;
	setInvitationToken: (token: string) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
	accessToken: localStorageStore.get("accessToken"),
	refreshToken: localStorageStore.get("refreshToken"),
	expiresAt: localStorageStore.get("expiresAt"),
	userId: localStorageStore.get("userId"),
	userName: localStorageStore.get("userName"),
	brandDetails: localStorageStore.get("brandDetails"),
	scope: localStorageStore.get("scope"),
	isForgotPasswordLoading: false,
	invitationToken: null,
	isBrand: localStorageStore.get("isBrand"),
	brandId: localStorageStore.get("brandId"),
	isSuperAdmin: localStorageStore.get("isSuperAdmin"),
	brandName: localStorageStore.get("brandName"),
	isLoggedIn: false,

	setAuth: ({ accessToken, refreshToken, expiresAt }: AuthTokens) => {
		localStorageStore.set("accessToken", accessToken);
		localStorageStore.set("refreshToken", refreshToken);
		localStorageStore.set("expiresAt", expiresAt);
		set({ accessToken, refreshToken, expiresAt });
	},

	setBrandDetails: (brandDetails: string) => {
		localStorageStore.set("brandDetails", brandDetails);
		set({ brandDetails });
	},

	setLogin: ({ isBrand, brandId, isSuperAdmin, brandName }: Login) => {
		localStorageStore.set("isBrand", isBrand);
		localStorageStore.set("isSuperAdmin", isSuperAdmin);
		localStorageStore.set("brandId", brandId);
		localStorageStore.set("brandName", brandName);
		set({ isBrand, brandId, isSuperAdmin, brandName });
	},

	setUser: (userId: string, userName: string) => {
		localStorageStore.set("userId", userId);
		localStorageStore.set("userName", userName);
		set({ userId, userName });
	},

	setScope: (scope: string) => {
		localStorageStore.set("scope", scope);
		set({ scope });
	},

	resetAuth: () => {
		localStorageStore.remove("accessToken");
		localStorageStore.remove("refreshToken");
		localStorageStore.remove("expiresAt");
		localStorageStore.remove("userId");
		localStorageStore.remove("userName");
		localStorageStore.remove("brandDetails");
		localStorageStore.remove("scope");
		localStorageStore.remove("isBrand");
		localStorageStore.remove("brandId");
		localStorageStore.remove("isSuperAdmin");
		localStorageStore.remove("brandName");
		set({
			accessToken: null,
			refreshToken: null,
			expiresAt: null,
			userId: null,
			userName: null,
			brandDetails: null,
			scope: null,
			isBrand: null,
			brandId: null,
			isSuperAdmin: null,
			brandName: null
		});
	},

	setIsForgotPasswordLoading: (isForgotPasswordLoading: boolean) => {
		set({ isForgotPasswordLoading });
	},

	setIsLoggedIn: (isLoggedIn: boolean) => {
		set({ isLoggedIn });
	},

	setInvitationToken: (token) => set({ invitationToken: token })
}));
